import { getCustomers, deleteCustomer as deleteCustomerApi } from '../services/api';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';
import { notifyDataChanged } from '../utils/dataEvents';
import type { CreatorInfo } from '../utils/permissions';
import { entityStoreMessages, removeItemsWhere } from './storeHelpers';
import { useProjectStoreBase } from './projectStore';
import { useQuoteStoreBase } from './quoteStore';
import { usePaymentStoreBase } from './paymentStore';
import { useOpportunityStoreBase } from './opportunityStore';
import { useTaskStoreBase } from './taskStore';

export interface Customer extends CreatorInfo {
  id: string;
  userId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  company?: string | null;
  source?: string | null;
  tags?: Array<{ id: string; name: string; color: string; category: string }>;
  archived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const customerMessages = {
  ...entityStoreMessages,
  deleteSuccess: () => i18n.t('customers.delete.success', { ns: 'pages' }),
};

const useCustomerStoreBase = createEntityStore<Customer>({
  entityName: 'customer',
  fetchFn: (page: number) => getCustomers(page),
  deleteFn: deleteCustomerApi,
  toast,
  messages: customerMessages,
});

// 封存客戶 store
const useArchivedCustomerStoreBase = createEntityStore<Customer>({
  entityName: 'archivedCustomer',
  fetchFn: (page: number) => getCustomers(page, { archived: true }),
  deleteFn: deleteCustomerApi,
  toast,
  messages: customerMessages,
});

// 客戶選擇器專用快取 store（對話框的客戶下拉）
interface CustomerSelectorState {
  allCustomers: Customer[];
  isLoadingAll: boolean;
  lastAllCustomersFetch: number | null;

  fetchAllCustomers: () => Promise<void>;
}

let allCustomersRequest: Promise<void> | null = null;

const useCustomerSelectorStore = create<CustomerSelectorState>()(
  persist(
    (set, get) => ({
      allCustomers: [],
      isLoadingAll: false,
      lastAllCustomersFetch: null,

      // stale-while-revalidate：有快取先用（不顯示載入中、下拉不閃），每次開對話框都在背景重抓；
      // 原本快取永久有效，同組織其他人新增的客戶要登出才看得到
      fetchAllCustomers: async () => {
        if (allCustomersRequest) return allCustomersRequest;
        if (!get().lastAllCustomersFetch) set({ isLoadingAll: true });
        allCustomersRequest = (async () => {
          try {
            const response = await getCustomers(1, { pageSize: 1000 }); // 後端單頁上限 1000
            set({ allCustomers: response.data.data, lastAllCustomersFetch: Date.now() });
          } catch (error) {
            console.error('載入客戶下拉選單失敗:', error); // 失敗時保留快取
          } finally {
            set({ isLoadingAll: false });
            allCustomersRequest = null;
          }
        })();
        return allCustomersRequest;
      },
    }),
    {
      name: 'customer-selector-store',
      partialize: (state) => ({
        allCustomers: state.allCustomers,
        lastAllCustomersFetch: state.lastAllCustomersFetch,
      }),
    }
  )
);

const updateSelector = (update: (customers: Customer[]) => Customer[]) =>
  useCustomerSelectorStore.setState(state => ({ allCustomers: update(state.allCustomers) }));

// 刪除客戶時後端連帶刪除專案、報價、收款、商機、任務：其他列表 store 同步移除
const removeCustomerData = (customerId: string) => {
  const belongsToCustomer = (item: any) => (item.customerId ?? item.customer?.id) === customerId;
  [useProjectStoreBase, useQuoteStoreBase, usePaymentStoreBase, useOpportunityStoreBase, useTaskStoreBase]
    .forEach(store => removeItemsWhere(store, belongsToCustomer));
  updateSelector(customers => customers.filter(c => c.id !== customerId));
};

/** 未封存、已封存兩個列表共用的刪除流程 */
const confirmDeleteFor = (store: typeof useCustomerStoreBase) => async () => {
  const customerId = store.getState().itemToDeleteId;
  await store.getState().confirmDelete();
  if (customerId) removeCustomerData(customerId);
  notifyDataChanged('customers');
};

// 導出基礎 store 供其他模組使用（標籤刪除時同步移除客戶上的標籤）
export { useCustomerStoreBase, useArchivedCustomerStoreBase };

export const useArchivedCustomerStore = () => {
  // 使用 Zustand 的選擇器訂閱，確保 pagination 變化時重新渲染
  const items = useArchivedCustomerStoreBase(state => state.items);
  const pagination = useArchivedCustomerStoreBase(state => state.pagination);
  const currentPage = useArchivedCustomerStoreBase(state => state.currentPage);
  const error = useArchivedCustomerStoreBase(state => state.error);
  const isLoading = useArchivedCustomerStoreBase(state => state.isLoading);
  const isPageLoading = useArchivedCustomerStoreBase(state => state.isPageLoading);
  const showDeleteConfirmModal = useArchivedCustomerStoreBase(state => state.showDeleteConfirmModal);
  const itemToDeleteId = useArchivedCustomerStoreBase(state => state.itemToDeleteId);
  const isDeleting = useArchivedCustomerStoreBase(state => state.isDeleting);

  return {
    customers: items,
    pagination,
    currentPage,
    error,
    isLoading,
    isPageLoading,
    showDeleteConfirmModal,
    itemToDeleteId,
    isDeleting,
    fetchCustomers: useArchivedCustomerStoreBase.getState().fetchItems,
    setCurrentPage: useArchivedCustomerStoreBase.getState().setCurrentPage,
    handleDelete: useArchivedCustomerStoreBase.getState().handleDelete,
    confirmDelete: confirmDeleteFor(useArchivedCustomerStoreBase),
    closeDeleteModal: useArchivedCustomerStoreBase.getState().closeDeleteModal,
    refreshData: useArchivedCustomerStoreBase.getState().refreshData,
  };
};

export const useCustomerStore = () => {
  // 使用 Zustand 的選擇器訂閱，確保 pagination 變化時重新渲染
  const items = useCustomerStoreBase(state => state.items);
  const pagination = useCustomerStoreBase(state => state.pagination);
  const currentPage = useCustomerStoreBase(state => state.currentPage);
  const error = useCustomerStoreBase(state => state.error);
  const isLoading = useCustomerStoreBase(state => state.isLoading);
  const isPageLoading = useCustomerStoreBase(state => state.isPageLoading);
  const showDeleteConfirmModal = useCustomerStoreBase(state => state.showDeleteConfirmModal);
  const itemToDeleteId = useCustomerStoreBase(state => state.itemToDeleteId);
  const isDeleting = useCustomerStoreBase(state => state.isDeleting);

  const selectorStore = useCustomerSelectorStore();

  return {
    customers: items,
    pagination,
    currentPage,
    error,
    isLoading,
    isPageLoading,
    showDeleteConfirmModal,
    itemToDeleteId,
    isDeleting,
    fetchCustomers: useCustomerStoreBase.getState().fetchItems,
    setCurrentPage: useCustomerStoreBase.getState().setCurrentPage,
    handleDelete: useCustomerStoreBase.getState().handleDelete,
    confirmDelete: confirmDeleteFor(useCustomerStoreBase),
    closeDeleteModal: useCustomerStoreBase.getState().closeDeleteModal,
    refreshData: useCustomerStoreBase.getState().refreshData,
    // 新增、編輯、回滾時主列表與選擇器快取一起更新
    updateCustomer: (id: string, customer: Partial<Customer>) => {
      useCustomerStoreBase.getState().updateItem(id, customer);
      updateSelector(customers => customers.map(c => (c.id === id ? { ...c, ...customer } : c)));
    },
    addCustomer: (customer: Customer) => {
      useCustomerStoreBase.getState().addItem(customer);
      updateSelector(customers => [...customers, customer]);
    },
    replaceCustomer: (tempId: string, realCustomer: Customer) => {
      useCustomerStoreBase.getState().updateItem(tempId, realCustomer);
      updateSelector(customers => customers.map(c => (c.id === tempId ? realCustomer : c)));
    },
    removeCustomer: (id: string) => {
      // 新增失敗回滾用
      removeItemsWhere<Customer>(useCustomerStoreBase, c => c.id === id);
      updateSelector(customers => customers.filter(c => c.id !== id));
    },
    getCustomerById: useCustomerStoreBase.getState().getItemById,
    allCustomers: selectorStore.allCustomers,
    isLoadingAllCustomers: selectorStore.isLoadingAll,
    fetchAllCustomers: selectorStore.fetchAllCustomers,
  };
};
